import type {MoneyTone} from '@pokpok/astryx';
import {useI18n} from '../i18n';
import {cx} from './cx';

/**
 * A money figure, in the only colours it is allowed to be.
 *
 * # Why this exists rather than `money()` plus a class
 *
 * Because the money rule is a colour decision, and a colour decision made in
 * thirteen screens is a colour decision made thirteen ways. `tone` is astryx's
 * `MoneyTone`, and the **only** sanctioned way to obtain a `verified` one is
 * `orderTotalTone(order)` / `paymentTone(payment)` from `src/order/moneyTone.ts`.
 * Passing the literal `'verified'` here is possible and is a defect: Pandan
 * states that pokpok's own server acknowledged the tender, and no device may
 * assert that from its own arithmetic.
 *
 * ```tsx
 * <Money satang={order.total_satang} tone={orderTotalTone(order)} size="lg" />
 * ```
 *
 * # Why `neutral` and `pending` look the same
 *
 * They do, and that is the design. A menu price, a cart estimate and a bill in
 * flight are all just amounts: ink, tabular, unremarkable. The thing that is
 * unresolved about a pending amount is said in *words* beside it — a
 * `StatusPill tone="warning"`, a line of copy — not by tinting the number amber
 * and leaving the guest to guess whether ฿185 is a warning. astryx's
 * `MoneyAmount` greys `pending`; this surface does not, because a greyed price
 * next to a black one reads as disabled.
 *
 * # Why not astryx's `MoneyAmount`
 *
 * It formats satang itself, with its own `Intl.NumberFormat`, which routes
 * around `src/i18n/format.ts` — the one place that decides `฿125` never renders
 * as `฿125.00`. Two formatters is how a bill and a receipt end up disagreeing by
 * a rendering rule. Everything on this surface goes through `money()`.
 */
export function Money({
  satang,
  tone = 'neutral',
  /** `lg` for the one figure a screen is about; `title` for a screen headline amount. */
  size = 'md',
  className,
  ariaLabel,
}: {
  /** Integer satang, straight from the contract. Never a baht float. */
  satang: number;
  tone?: MoneyTone;
  size?: 'md' | 'title' | 'lg';
  className?: string;
  /** Localised, for when the figure alone does not say what it is. */
  ariaLabel?: string;
}) {
  const {money} = useI18n();
  return (
    <span
      aria-label={ariaLabel}
      className={cx(
        'pk-money',
        `pk-money--${tone}`,
        size === 'title' && 'pk-title',
        size === 'lg' && 'pk-h1',
        className,
      )}
    >
      {money(satang)}
    </span>
  );
}

/**
 * A price **difference**: `+฿10`, `−฿5`, `฿0`.
 *
 * Only for modifier deltas, and always ink or River Stone — a delta is never
 * verified money, so this takes no tone. The sign leads the symbol (`−฿5`, not
 * `฿-5`) and uses a true minus; the reasoning is on `formatSignedSatang` in
 * `src/i18n/format.ts`.
 */
export function MoneyDelta({satang, className}: {satang: number; className?: string}) {
  const {signedMoney} = useI18n();
  return <span className={cx('pk-money', 'pk-money--neutral', className)}>{signedMoney(satang)}</span>;
}
